/**
 * BundleConfigurator — renders one radio-button group per bundle slot
 * and reports the selected option per slot + live total price delta.
 *
 * The parent component owns the selection state (so it can use it for
 * checkout or save it into a quote line item) and passes it in via
 * `value` + `onChange`.
 *
 * Shared between the storefront (ProductDetailPage) and the dealer
 * quote builder.
 */

import type { Json } from '@sifly/shared-types'
import { computeBundleDiscount } from '@sifly/shared-utils'
import type { BundleDiscountRule, BundleSelectionEntry } from '@sifly/shared-utils'
import { SelectionChip } from './SelectionChip'

// Shape the configurator expects per slot, after joining bundle_slot_options
// with the referenced sku record.
export type ConfiguratorSlot = {
  id: string
  label: string
  is_required: boolean
  display_order: number
  options: ConfiguratorOption[]
}

export type ConfiguratorOption = {
  id: string                  // bundle_slot_option id
  sku_id: string
  sku_code: string
  sku_name: string
  label: string               // display label, e.g. "Pearl White"
  price_delta: number         // in whole dollars, may be negative
  is_default: boolean
  display_order: number
  attributes?: Json
}

// Selection map: slot_id → option_id (null = nothing picked for that slot)
export type BundleSelection = Record<string, string | null>

/** Build a default selection from a list of slots, picking each slot's is_default (or first). */
export function buildDefaultSelection(slots: ConfiguratorSlot[]): BundleSelection {
  const sel: BundleSelection = {}
  for (const slot of slots) {
    if (slot.options.length === 0) {
      sel[slot.id] = null
      continue
    }
    const def = slot.options.find(o => o.is_default) ?? (slot.is_required ? slot.options[0] : null)
    sel[slot.id] = def?.id ?? null
  }
  return sel
}

/** Sum the price_deltas of the currently selected options across all slots. */
export function computeBundleDelta(slots: ConfiguratorSlot[], selection: BundleSelection): number {
  let delta = 0
  for (const slot of slots) {
    const selectedId = selection[slot.id]
    if (!selectedId) continue
    const opt = slot.options.find(o => o.id === selectedId)
    if (opt) delta += opt.price_delta
  }
  return delta
}

/**
 * Convert a `BundleSelection` map into the entry list the shared discount
 * evaluator takes. Kept here so every caller shapes it the same way — the
 * evaluator matches on slot_id, so a mis-shaped selection silently returns no
 * discount rather than erroring.
 */
export function selectionEntries(selection: BundleSelection): BundleSelectionEntry[] {
  return Object.entries(selection).map(([slot_id, option_id]) => ({ slot_id, option_id }))
}

/**
 * The bundle's net price adjustment: option deltas MINUS any conditional combo
 * discount. This is the display-side twin of the server calculation in
 * `checkout.ts`; both call `computeBundleDiscount` so the customer is quoted
 * what they'll be charged.
 */
export function computeBundleDeltaWithDiscounts(
  slots: ConfiguratorSlot[],
  selection: BundleSelection,
  rules: BundleDiscountRule[],
): { delta: number; discount: number; applied: BundleDiscountRule[] } {
  const delta = computeBundleDelta(slots, selection)
  const { total: discount, applied } = computeBundleDiscount(selectionEntries(selection), rules)
  return { delta: delta - discount, discount, applied }
}

/** Return the resolved option records for each selected slot (for checkout / quote snapshot). */
export function resolveBundleSelections(
  slots: ConfiguratorSlot[],
  selection: BundleSelection,
): { slot: ConfiguratorSlot; option: ConfiguratorOption }[] {
  const out: { slot: ConfiguratorSlot; option: ConfiguratorOption }[] = []
  for (const slot of slots) {
    const selectedId = selection[slot.id]
    if (!selectedId) continue
    const option = slot.options.find(o => o.id === selectedId)
    if (option) out.push({ slot, option })
  }
  return out
}

/** True if every required slot has a selection. */
export function isBundleSelectionComplete(slots: ConfiguratorSlot[], selection: BundleSelection): boolean {
  for (const slot of slots) {
    // A slot with no options is not a question. It is a catalog mistake, but
    // the customer cannot answer it and cannot see it, so blocking the buy
    // button on it strands them with no way forward. Required-ness only binds
    // where there is actually something to pick.
    if (slot.options.length === 0) continue
    if (slot.is_required && !selection[slot.id]) return false
  }
  return true
}

type Props = {
  slots: ConfiguratorSlot[]
  value: BundleSelection
  onChange: (next: BundleSelection) => void
  /** Conditional combo discounts for this bundle. Omit where none are loaded. */
  discountRules?: BundleDiscountRule[]
}

export default function BundleConfigurator({ slots, value, onChange, discountRules }: Props) {
  function select(slotId: string, optionId: string | null) {
    onChange({ ...value, [slotId]: optionId })
  }

  const { applied } = computeBundleDiscount(selectionEntries(value), discountRules ?? [])

  return (
    <div className="space-y-6">
      {slots.map(slot => {
        const selectedId = value[slot.id]
        // Skip rendering slots that have only one mandatory option — there's nothing to choose.
        if (slot.is_required && slot.options.length === 1) {
          return null
        }
        // Likewise a slot with no options at all: rendering it produced an
        // empty section the customer could stare at but never act on.
        if (slot.options.length === 0) {
          return null
        }
        return (
          <fieldset key={slot.id}>
            <legend className="text-sm font-medium text-[var(--color-text)] mb-2">
              {slot.label}
              {!slot.is_required && (
                <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)]">(optional)</span>
              )}
            </legend>
            <div className="flex flex-wrap gap-2">
              {/* For optional slots, allow an explicit "None" button */}
              {!slot.is_required && (
                <SelectionChip selected={!selectedId} onClick={() => select(slot.id, null)}>
                  None
                </SelectionChip>
              )}
              {slot.options.map(opt => (
                <SelectionChip
                  key={opt.id}
                  selected={selectedId === opt.id}
                  onClick={() => select(slot.id, opt.id)}
                  trailing={opt.price_delta !== 0
                    ? `${opt.price_delta > 0 ? '+' : '−'}$${Math.abs(opt.price_delta).toLocaleString('en-US')}`
                    : undefined}
                >
                  {opt.label}
                </SelectionChip>
              ))}
            </div>
          </fieldset>
        )
      })}
      {/* Earned combo savings. Calm and factual — a quiet confirmation the
          lower total is intentional, not an alarm or a sales shout. */}
      {applied.map(rule => (
        <div
          key={rule.id}
          className="flex items-center justify-between rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-sm"
        >
          <span className="text-[var(--color-text-muted)]">{rule.label}</span>
          <span className="font-medium text-[var(--color-text)]">
            −${rule.discount_amount.toLocaleString('en-US')}
          </span>
        </div>
      ))}
    </div>
  )
}
